'use client';
import { RefObject, useEffect, useRef } from 'react';

/**
 * Detects clicks outside a referenced element and invokes a callback.
 *
 * @param reference - Ref to the element considered "inside" (e.g., a popover container).
 *   Clicks within this element are ignored.
 *   Example: a popover body that should stay open when clicked inside.
 *
 * @param callback - Called when a click lands outside `reference` (and outside `excludeRef`).
 *   Example: `() => setIsOpen(false)` to close the popover.
 *
 * @param toggleRef - Optional ref to an element that should NOT trigger the outside-click
 *   callback, even though it's outside `reference`. Use this for toggle buttons that have
 *   their own onClick handler — without it, clicking the toggle fires both the outside-click
 *   (closing) and the toggle's onClick (reopening), causing the popover to never close.
 *   Example: a caret/icon button that opens and closes the popover.
 */
export const useOutsideClick = (
  reference: RefObject<HTMLDivElement | null>,
  callback: () => void,
  toggleReference?: RefObject<HTMLElement | null>,
) => {
  const callbackReference = useRef(callback);
  callbackReference.current = callback;

  useEffect(() => {
    const onPointerUp = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        reference.current &&
        !reference.current.contains(target) &&
        (!toggleReference?.current || !toggleReference.current.contains(target))
      ) {
        callbackReference.current();
      }
    };

    document.addEventListener('pointerup', onPointerUp);

    return () => {
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [reference, toggleReference]);
};
